<template>
  <slot
    :attrs="attrs"
    :open-tooltip="openTooltip"
    :close-tooltip="closeTooltip"
  />
  <Teleport to="body">
    <div
      v-show="isOpened"
      :id="parsedId"
      ref="tooltip"
      :style="{
        position: strategy,
        top: `${y ?? 0}px`,
        left: `${x ?? 0}px`,
        width: 'max-content',
      }"
      :aria-hidden="!isOpened"
      class="tooltip"
      role="tooltip"
    >
      {{ props.text }}
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, VNodeRef } from 'vue';
import { useFloating, offset, autoUpdate } from '@floating-ui/vue';
import { getUid } from '~/utils/common';

type Props = {
  text: string;
  id?: string;
};

const props = defineProps<Props>();

const parsedId = computed<string>(() => {
  return props.id ?? `tooltip-hint-${getUid()}`;
});

const tooltip = ref<null | HTMLElement>(null);
const reference = ref<null | Element>(null);
const referenceRef: VNodeRef = (el) => {
  if (!el) {
    return;
  }
  if (el instanceof Element) {
    reference.value = el;
    return;
  }
  reference.value = el.$el;
};

const { x, y, strategy } = useFloating(reference, tooltip, {
  strategy: 'fixed',
  placement: 'top',
  whileElementsMounted: autoUpdate,
  middleware: [offset(10)],
});

const isOpened = ref(false);

const attrs = computed(() => ({
  ref: referenceRef,
  'aria-describedby': parsedId,
}));

const openTooltip = () => {
  isOpened.value = true;
};

const closeTooltip = () => {
  isOpened.value = false;
};
</script>

<style scoped>
.tooltip {
  position: relative;
  z-index: 99999;
  background-color: var(--secondary-color);
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  color: white;
  text-align: center;
  max-width: 15rem;
  word-break: break-words;
}

.tooltip::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  border-left: 0.5rem solid transparent;
  border-right: 0.5rem solid transparent;
  border-top: 0.5rem solid var(--secondary-color);
}
</style>
